/**
 * The cart page, alongside Horizon's own cart.
 *
 * Horizon's cart keeps its lines, quantities, discount and totals as they
 * are. This adds what the theme's cart needs around them:
 *
 *   Save For Later puts a line on the wishlist, with its size, then takes it
 *   out of the bag through the line's own remove button, so Horizon animates
 *   the row and updates the totals as it does for any removal.
 *
 *   The header bag and the You might also like row follow every change
 *   Horizon makes to the bag, which only Horizon's own parts hear about.
 *
 *   A change made outside Horizon, from the Added to bag panel or a card on
 *   this page, redraws Horizon's cart and the row, since Horizon does not
 *   hear about those either.
 *
 * Both redraws use Horizon's own section renderer, loaded through the
 * theme's import map, so the cart morphs the way Horizon morphs it.
 */
(() => {
  const cartSection = () => document.querySelector('cart-items-component[data-section-id]');
  const feed = () => document.querySelector('[data-nyl-cart-feed][data-section-id]');

  let renderer = null;
  const sectionRenderer = () => {
    renderer ||= import('@theme/section-renderer')
      .then((module) => module.sectionRenderer)
      .catch(() => null);
    return renderer;
  };

  const redraw = async (sectionId) => {
    if (!sectionId) return;
    const tool = await sectionRenderer();
    if (tool) {
      await tool.renderSection(sectionId, { cache: false });
    } else {
      // Without Horizon's renderer, the page itself is the safe way to show
      // the bag as it stands.
      window.location.reload();
    }
  };

  const refreshBadge = () => {
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  };

  const refreshFeed = async () => {
    const row = feed();
    if (!row) return;
    try {
      await redraw(row.dataset.sectionId);
      // Fresh cards bring hearts and swatches that have to read their state.
      document.dispatchEvent(new CustomEvent('nyl:content', { bubbles: true }));
    } catch (error) {
      // The row keeps what it showed. The bag itself is unaffected.
    }
  };

  const refreshCart = async () => {
    const cart = cartSection();
    try {
      if (cart) await redraw(cart.dataset.sectionId);
    } catch (error) {
      // Horizon's cart keeps its last state rather than breaking.
    }
    refreshFeed();
  };

  /* Save For Later ------------------------------------------------------ */

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-nyl-save-later]');
    if (!button || button.disabled) return;

    const row = button.closest('[data-key]');
    const remove = row?.querySelector('.cart-items__remove');
    const wishlist = window.NylWishlist;
    // Both halves or neither: never a saved piece that stays in the bag, or
    // a removed one that was not saved.
    if (!remove || remove.hidden || !wishlist) return;

    button.disabled = true;
    wishlist.add(button.dataset.productHandle, button.dataset.variantId);
    remove.click();

    // The message sits under the header's heart, where the piece has gone.
    // The row it came from is on its way out.
    const heart = document.querySelector('[data-nyl-wishlist-link]');
    wishlist.notify?.(heart || button, button.dataset.saved);
  });

  /* Horizon's changes to the bag ---------------------------------------- */

  import('@shopify/events')
    .then(({ StandardEvents }) => {
      const name = StandardEvents?.cartLinesUpdate;
      if (!name) return;
      document.addEventListener(name, (event) => {
        Promise.resolve(event.promise)
          .then(() => {
            refreshBadge();
            refreshFeed();
          })
          .catch(() => {
            // A failed change leaves the bag as it was, so nothing to follow.
          });
      });
    })
    .catch(() => {
      // Without the events module the page still works; the badge and the
      // row catch up on the next page load.
    });

  /* Changes from outside Horizon ---------------------------------------- */

  document.addEventListener('nyl:cart:added', refreshCart);
  document.addEventListener('nyl:cart:removed', refreshCart);

  // An app changed the bag's prices after the change the cart was drawn
  // from, as Discount Ninja does (nyl-cart-watch.js). Only the cart moves,
  // and never by reloading: the app writes after every page load too.
  document.addEventListener('nyl:cart:changed', async () => {
    const cart = cartSection();
    const tool = cart ? await sectionRenderer() : null;
    if (!tool) return;
    try {
      await tool.renderSection(cart.dataset.sectionId, { cache: false });
    } catch (error) {
      // The cart keeps what it showed; the next change or load catches up.
    }
  });
})();
